import { storage, db } from "../../lib/firebase";
import { ref, uploadString, listAll } from "firebase/storage";
import {
  collection,
  doc,
  getDocs,
  limit,
  query,
  setDoc,
  serverTimestamp,
  where,
} from "firebase/firestore";
import { QRCodeGenerator } from "./generator";
import { useDebugStore } from "../../store/debugStore";
import { UniversalDirectoryManager } from "../../services/storage/universalDirectoryManager";
import { FileNaming } from "../../utils/UniversalFileNaming";
import { QRCodeService } from "../qrCodeService";
import type { VCardCategory } from "../../types/vcard";

interface CreateQRCodeParams {
  userId: string;
  category: string;
  content: string;
  name: string;
  description?: string;
}

export class QRCodeInitialization {
  private static activeOperations = new Map<string, Promise<string>>();

  /**
   * Gets a unique key for tracking operations
   */
  private static getOperationKey(userId: string, category: string): string {
    return `${userId}_${category}`;
  }

  /**
   * Initializes default QR code URLs for all categories during onboarding
   */
  static async initializeDefaultQRCodes(userId: string): Promise<boolean> {
    const addDebugMessage = useDebugStore.getState().addMessage;
    const categories =
      UniversalDirectoryManager.STRUCTURE.categoryFolders.qrcodesMetadata;

    try {
      addDebugMessage("info", "Initializing default QR codes for new user");

      for (const category of categories) {
        // Look up the vcard for this category to get the token URL
        const vcardCollectionPath = UniversalDirectoryManager.getPath({
          userId,
          contentType: "vcard",
        });
        const vcardSnap = await getDocs(
          query(
            collection(db, vcardCollectionPath),
            where("category", "==", category),
            limit(1),
          ),
        );
        let url: string;
        if (!vcardSnap.empty) {
          const vcardId = vcardSnap.docs[0].id;
          const qrData = await QRCodeService.createQRCode({
            userId,
            vcardId,
            category: category as VCardCategory,
          });
          url = qrData.url;
        } else {
          url = `${window.location.origin}/qr/${category}/${userId}`;
        }

        // Write to canonical metadata doc: users/{userId}/qrcodesMetadata/{category}
        const metadataCollectionPath = UniversalDirectoryManager.getPath({
          userId,
          contentType: "qrcode",
          isMetadata: true,
        });
        const metadataRef = doc(db, metadataCollectionPath, category);
        await setDoc(metadataRef, {
          id: `default_${category}`,
          userId,
          category,
          url,
          version: 1,
          createdAt: serverTimestamp(),
          metadata: {
            printHistory: [],
            lastScanned: null,
            scanHistory: [],
          },
        });

        addDebugMessage("info", `Created default QR code URL for ${category}`);
      }

      addDebugMessage(
        "success",
        "Default QR code URLs initialized successfully",
      );
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addDebugMessage(
        "error",
        `Failed to initialize default QR codes: ${errorMessage}`,
      );
      return false;
    }
  }

  /**
   * Initializes QR code metadata directories for a new user
   */
  static async initializeQRCodeMetadata(userId: string): Promise<boolean> {
    const addDebugMessage = useDebugStore.getState().addMessage;
    const categories =
      UniversalDirectoryManager.STRUCTURE.categoryFolders.qrcodesMetadata;

    try {
      addDebugMessage("info", "Initializing QR code metadata directories");

      // Create metadata documents for each category
      for (const category of categories) {
        const metadataCollectionPath = UniversalDirectoryManager.getPath({
          userId,
          contentType: "qrcode",
          isMetadata: true,
        });
        const metadataRef = doc(db, metadataCollectionPath, category);

        // Initialize with empty metadata
        await setDoc(metadataRef, {
          category,
          lastUpdated: serverTimestamp(),
          qrCodeCount: 0,
          isInitialized: true,
        });

        addDebugMessage("info", `Created QR metadata for ${category}`);
      }

      // Initialize default QR codes with URLs
      await this.initializeDefaultQRCodes(userId);

      addDebugMessage(
        "success",
        "QR code metadata directories initialized successfully",
      );
      return true;
    } catch (error) {
      const errorMessage =
        error instanceof Error ? error.message : "Unknown error";
      addDebugMessage(
        "error",
        `Failed to initialize QR code metadata: ${errorMessage}`,
      );
      return false;
    }
  }

  /**
   * Checks if a filename matches the expected pattern without considering the unique ID
   */
  private static isValidSVGFilename(
    filename: string,
    userId: string,
    category: string,
  ): boolean {
    // Pattern: user_[userId]_[firstName]_[lastName]_[category]_[optionalItem]_[date]_[uniqueId].svg
    const normalizedCategory = category.toLowerCase();
    const pattern = new RegExp(
      `^user_${userId}_.*_.*_${normalizedCategory}(?:_[a-z0-9-]+)?_\\d{8}_[A-Za-z0-9_-]+\\.svg$`,
      "i",
    );
    return pattern.test(filename) && !filename.includes("placeholder");
  }

  static async createQRCode(params: CreateQRCodeParams): Promise<string> {
    const { userId, category, content, name, description } = params;
    const addDebugMessage = useDebugStore.getState().addMessage;
    const operationKey = this.getOperationKey(userId, category);

    // Check if there's already an operation in progress
    const existingOperation = this.activeOperations.get(operationKey);
    if (existingOperation) {
      addDebugMessage(
        "info",
        `QR code creation already in progress for ${category}`,
      );
      return existingOperation;
    }

    // Create new operation and store it
    const operation = (async () => {
      try {
        // Get path using UniversalDirectoryManager
        const svgPath = UniversalDirectoryManager.getPath({
          userId,
          contentType: "svg",
          category,
        });

        // Double-check for existing valid QR code
        const categoryRef = ref(storage, svgPath);
        const files = await listAll(categoryRef);

        // Check if a valid SVG already exists in this category
        const hasValidQRCode = files.items.some((item) =>
          this.isValidSVGFilename(item.name, userId, category),
        );

        if (hasValidQRCode) {
          // Find and return the existing file path
          const existingFile = files.items.find((item) =>
            this.isValidSVGFilename(item.name, userId, category),
          );
          addDebugMessage(
            "info",
            `Valid QR code SVG already exists for ${category}`,
          );
          return `${svgPath}/${existingFile!.name}`;
        }

        // Generate QR code SVG
        const svgContent = await QRCodeGenerator.generateQRCode(content);

        // Parse name into firstName and lastName if provided
        const [firstName, lastName] = name.split("_");

        // Generate filename using FileNaming
        const filename = FileNaming.generate("svg", {
          userId,
          firstName,
          lastName,
          category,
          item: "default",
        });

        // Upload SVG to Firebase Storage
        const filePath = `${svgPath}/${filename}`;
        const fileRef = ref(storage, filePath);

        await uploadString(fileRef, svgContent, "raw", {
          contentType: "image/svg+xml",
          customMetadata: {
            description: description ?? "",
            category,
            createdAt: new Date().toISOString(),
          },
        });

        // Update metadata
        const metadataPath = `${UniversalDirectoryManager.STRUCTURE.base}/${userId}/qrcodesMetadata/${category}`;
        const metadataRef = doc(db, metadataPath);
        await setDoc(
          metadataRef,
          {
            category,
            lastUpdated: serverTimestamp(),
            qrCodeCount: 1,
            isInitialized: true,
            lastQRCode: filename,
          },
          { merge: true },
        );

        addDebugMessage("info", `✅ Created QR code: ${filename}`);
        return filePath;
      } catch (error) {
        const errorMessage =
          error instanceof Error ? error.message : "Unknown error";
        addDebugMessage(
          "error",
          `❌ Failed to create QR code: ${errorMessage}`,
        );
        throw error;
      } finally {
        // Clean up operation tracking
        this.activeOperations.delete(operationKey);
      }
    })();

    // Store the operation
    this.activeOperations.set(operationKey, operation);
    return operation;
  }
}
